import React, { FC, useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import {
  GenericResponse,
  OnboardingPasswordDto,
  parseJwt,
  UserResetPasswordUpdateTokenResponseDto,
  UserResetPasswordValidateTokenRequestDto,
  UserResetPasswordValidateTokenResponseDto,
} from '@sparling/financial-common'
import { toast } from 'react-toastify'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useRest } from '../../components/Context'
import { ResetPasswordComponent } from '../../components/ResetPassword'
import { Loader } from '../../components/Loader'
import { routes } from '../../constants/routes'

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    color: '#584DE2',
  },
  container: {
    maxWidth: 1184,
    margin: '0 auto',
    minHeight: 'calc(100vh - 248px)',
  },
  titleContainer: {
    position: 'relative',
    paddingTop: 150,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -100%)',
  },
  title: {
    padding: '25px 0',
    marginTop: '0 auto',
    fontSize: 40,
    fontWeight: 'bold',
    letterSpacing: 0,
    color: theme.palette.primary.dark,
  },
  text: {
    fontSize: 20,
  },
}))

interface UserTokenData {
  email: string
  firstName: string
  lastName: string
}

export const ResetPassword: FC = (): JSX.Element => {
  const classes = useStyles()
  const { rxios } = useRest()
  const { userIdReference } = useParams() as { [key: string]: string }
  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)
  const token = stringParams.get('token')
  const [entityResetPassword, setEntityResetPassword] = useState<OnboardingPasswordDto>(new OnboardingPasswordDto())
  const [isLinkValid, setIsLinkValid] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [isPasswordChanged, setIsPasswordChanged] = useState<boolean>(false)

  useEffect(() => {
    setLoading(true)
    if (token) {
      const dataToken: UserTokenData = parseJwt(token)
      if (dataToken) {
        setEntityResetPassword({
          firstName: dataToken.firstName,
          lastName: dataToken.lastName,
          email: dataToken.email,
          password: '',
          confirmPassword: '',
        })
        const data: UserResetPasswordValidateTokenRequestDto = {
          userIdReference,
          token,
        }
        rxios.post('user/password/reset/validate', data).subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setLoading(false)
            const result: GenericResponse<UserResetPasswordValidateTokenResponseDto> = next
            if (result && result.data.isValid) {
              setIsLinkValid(true)
            } else {
              setIsLinkValid(false)
              toast.error('Provided link is expired')
            }
          },
          () => {
            setLoading(false)
            setIsLinkValid(false)
            toast.error('Provided link is expired')
          },
        )
      }
    }
  }, [rxios, token, userIdReference])

  const onSubmit = useCallback(
    // eslint-disable-next-line
    (newValue: any) => {
      if (newValue.password === newValue.confirmPassword) {
        setLoading(true)
        rxios
          .put('user/password', {
            userIdReference,
            token,
            password: newValue.password,
          })
          .subscribe(
            // eslint-disable-next-line
            (next: any) => {
              const result: GenericResponse<UserResetPasswordUpdateTokenResponseDto> = next
              setLoading(false)
              if (result && result.data.result) {
                setIsPasswordChanged(true)
              } else {
                setIsPasswordChanged(false)
                toast.error('Can not change password')
              }
            },
            () => {
              setLoading(false)
              setIsPasswordChanged(false)
              toast.error('Can not change password')
            },
          )
      }
    },
    [rxios, token, userIdReference],
  )

  return (
    <>
      {loading && <Loader />}
      {isLinkValid && !isPasswordChanged && <ResetPasswordComponent onSubmit={onSubmit} model={entityResetPassword} />}

      {!isLinkValid && !isPasswordChanged && !loading && (
        <Grid className={classes.container} container direction="column" alignItems="center">
          <Grid className={classes.titleContainer} container justifyContent={'center'} alignItems={'center'}>
            <Typography variant="h1" gutterBottom className={classes.title}>
              Reset Password
            </Typography>
          </Grid>
          <Grid className={classes.contentContainer} container justifyContent={'center'} alignItems={'center'}>
            <Typography className={classes.text}>Provided link is expired or not valid.</Typography>
            <Typography className={classes.text}>
              If you have any difficulties with reset password functionality, please{' '}
              <NavLink className={classes.link} to={routes.CONTACT}>
                contact support
              </NavLink>
              .
            </Typography>
          </Grid>
        </Grid>
      )}
      {isLinkValid && isPasswordChanged && (
        <Grid className={classes.container} container direction="column" alignItems="center">
          <Grid className={classes.titleContainer} container justifyContent={'center'} alignItems={'center'}>
            <Typography variant="h1" gutterBottom className={classes.title}>
              Reset Password
            </Typography>
          </Grid>
          <Grid className={classes.contentContainer} container justifyContent={'center'} alignItems={'center'}>
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <Typography className={classes.text}>Password was changed successfully!</Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  )
}
